import axios from 'axios';

const host = window.location.hostname;
const isLocalHost = host === 'localhost' || host === '127.0.0.1';
const isPrivateIp = /^(10\.|192\.168\.|172\.(1[6-9]|2\d|3[0-1])\.)/.test(host);
const isLocalNetworkHost = isLocalHost || isPrivateIp;

export const API_BASE_URL = process.env.REACT_APP_API_BASE_URL
  || (isLocalNetworkHost ? `http://${host}:8000` : 'https://memo.shbb.pro/api');

export const STATIC_BASE_URL = process.env.REACT_APP_STATIC_BASE_URL
  || `${API_BASE_URL}/static`;

const api = axios.create({
  baseURL: API_BASE_URL,
});

// Добавляем интерсептор для автоматической вставки токена
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('access_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  console.log('Final request headers:', config.headers);
  return config;
});

api.interceptors.response.use(
  response => response,
  error => {
    if (error.response && error.response.status === 401 && error.config.url !== '/token/') {
      localStorage.removeItem('access_token');
      window.location.href = '/';  // редирект на главную
    }
    return Promise.reject(error);
  }
);

export const login = async (email, password) => {
  const params = new URLSearchParams();
  params.append('username', email);
  params.append('password', password);

  const response = await api.post('/token/', params, {
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded'
    }
  });
  console.log('Login response:', response.data);
  return response.data;
};

export const register = async (email, password, name, uiLang = 'ru') => {
  return api.post('/register/', {
    email,
    password,
    name,
    ui_lang: uiLang
  }, {
    headers: { 'Content-Type': 'application/json' }
  });
};

export const requestPasswordReset = async (email) => {
  const response = await api.post('/request-password-reset/', { email });
  return response.data;
};

export const resetPassword = async (token, new_password) => {
  const response = await api.post('/reset-password/', { token, new_password });
  return response.data;
};

// Языки
export const getAllLanguages = async () => {
  const response = await api.get('/languages/');
  return response.data;
};

export const getLanguage = async (language_id) => {
  const response = await api.get(`/languages/${language_id}`);
  return response.data;
};

// Темы
export const getThemesByLanguage = async (language_id, skip = 0, limit = 1000) => {
  console.log("Requesting themes for language:", language_id);
  const response = await api.get(`/languages/${language_id}/themes/`, {
    params: { skip, limit }
  });
  return response.data;
};


// Фильтры
export const getFiltersByUser = async (skip = 0, limit = 1000) => {
  const response = await api.get('/filters/', {
    params: { skip, limit }
  });
  console.log('[getFiltersByUser] response:', response.data);
  return response.data;
};


// Слова
export const getWordsByTheme = async (theme_id, skip = 0, limit = 1000) => {
  const response = await api.get(`/themes/${theme_id}/words/`, {
    params: { skip, limit }
  });
  return response.data;
};

export const getWordsByFilter = async (filter_id, skip = 0, limit = 1000) => {
  const response = await api.get(`/filters/${filter_id}/words/`, {
    params: { skip, limit }
  });
  return response.data;
};

export const getWordsByThemeAndFilter = async (theme_id, filter_id, skip = 0, limit = 1000) => {
  const response = await api.get(`/themes/${theme_id}/filters/${filter_id}/words/`, {
    params: { skip, limit }
  });
  return response.data;
};

export const getReviewWords = async (skip = 0, limit = 1000) => {
  const response = await api.get('/words/review/', {
    params: { skip, limit }
  });
  return response.data;
};

export const getReviewWordIds = async () => {
  const response = await api.get('/words/review/ids');
  return response.data;
};

export const addWordToReview = async (wordId) => {
  const response = await api.post(`/words/${wordId}/review`);
  return response.data;
};

export const removeWordFromReview = async (wordId) => {
  await api.delete(`/words/${wordId}/review`);
};


// Добавление нового фильтра
export const createFilter = async (filterName) => {
  const response = await api.post('/filters/', {
    filter_name: filterName
  });
  console.log('[createFilter] response:', response.data);
  return response.data;
};

export const deleteFilter = async (filterId) => {
  await api.delete(`/filters/${filterId}`);
};


// Создание темы
export const createTheme = async (theme) => {
  const response = await api.post('/themes/', theme); // theme: { name, language_id }
  console.log('[createTheme] response:', response.data);
  return response.data;
};

export const deleteTheme = async (themeId) => {
  await api.delete(`/themes/${themeId}`);
};

// Создание слова
export const createWord = async (word) => {
  const response = await api.post('/words/', word);  // word = { russian_word, foreign_word, language_id, theme_id, filter_id? }
  return response.data;
};

export const deleteWord = async (wordId) => {
  await api.delete(`/words/${wordId}`);
};

// Topics
export const getTopicsByLanguage = async (language_id, skip = 0, limit = 1000) => {
  const response = await api.get(`/languages/${language_id}/topics/`, {
    params: { skip, limit }
  });
  return response.data;
};

export const generateTopicFromReviewWords = async (language_id) => {
  const response = await api.post('/topics/generate', { language_id });
  return response.data;
};

export const changePassword = async (currentPassword, newPassword) => {
  const response = await api.post('/users/change-password', {
    current_password: currentPassword,
    new_password: newPassword,
  });
  return response.data;
};

export const getThemePreference = async () => {
  const response = await api.get('/users/theme-preference');
  return response.data;
};

export const updateThemePreference = async (themeMode) => {
  const response = await api.put('/users/theme-preference', {
    theme_mode: themeMode,
  });
  return response.data;
};

export const getAdminUsers = async (skip = 0, limit = 100) => {
  const response = await api.get('/admin/users/', {
    params: { skip, limit },
  });
  return response.data;
};

export const sendFeedback = async (payload) => (await api.post('/feedback', payload)).data;

export const verifyEmail = async (email, code) =>
  (await api.get('/mail_validator', { params: { email, code }, headers: { Accept: 'application/json' } })).data;

// ---- Занятия (study) ----
export const getStudySettings = async () => (await api.get('/study/settings')).data;
export const updateStudySettings = async (payload) => (await api.put('/study/settings', payload)).data;
export const getStudyQueue = async ({ limit = 15, exclude = [], extraNew = 0 } = {}) => {
  const params = { limit, extra_new: extraNew };
  if (exclude.length) params.exclude = exclude.join(',');
  return (await api.get('/study/queue', { params })).data;
};
export const submitStudyAnswer = async (payload) => (await api.post('/study/answer', payload)).data;
export const overrideStudyRating = async (attemptId, rating) =>
  (await api.post(`/study/attempts/${attemptId}/rating`, { rating })).data;
export const startStudySession = async () => (await api.post('/study/sessions')).data;
export const updateStudySession = async (sessionId, activeSeconds, finish = false) =>
  (await api.put(`/study/sessions/${sessionId}`, { active_seconds: activeSeconds, finish })).data;
export const getStudyProgress = async () => (await api.get('/study/progress')).data;
export const getPetrovCourse = async () => (await api.get('/study/courses/petrov')).data;
export const getCurrentCourse = async () => (await api.get('/study/courses/current')).data;
export const getLeaderboard = async () => (await api.get('/study/leaderboard')).data;
export const getPersonalItems = async () => (await api.get('/study/items')).data;
export const createPersonalItem = async (payload) => (await api.post('/study/items', payload)).data;
export const deletePersonalItem = async (itemId) => api.delete(`/study/items/${itemId}`);
